import { useIntl } from 'react-intl';

import type {
  DashboardFlashAlertMessageType,
  SourcingStats,
} from 'api-clients/monolith';

export const useAutomationFlashMessages = () => {
  const intl = useIntl();

  const getAutomationFlashMessages = (
    flashAlertMessageType?: DashboardFlashAlertMessageType | null,
    stats?: SourcingStats,
  ) => {
    const {
      automation_type: automationType,
      automation_action: automationAction,
      threshold_percentage: thresholdPercentage,
    } = flashAlertMessageType ?? {};

    switch (automationType) {
      case 'automated_as_goal_met': {
        if (automationAction === 'paused') {
          return {
            description: intl.formatMessage({
              defaultMessage:
                'Sourcing has been paused automatically as we have sufficient applicants in the funnel.',
              description:
                'Dashboard inline alert: auto-paused because applicant volume met the goal',
            }),
          };
        }
        break;
      }
      case 'hiring_goal_ended': {
        if (automationAction === 'paused') {
          return {
            description: intl.formatMessage({
              defaultMessage:
                'Your hiring goal has ended. Create a new hiring goal to continue hiring.',
              description:
                'Dashboard inline alert: goal window closed; user should start a new goal',
            }),
          };
        }
        if (automationAction === 'breached') {
          const { expected, actual } = stats ?? {};
          const daysLeft =
            expected != null && actual != null ? expected - actual : null;
          if (daysLeft === 0) {
            return {
              description: intl.formatMessage({
                defaultMessage:
                  'Your hiring goal ends today. Extend the duration if you need more time to hire.',
                description:
                  'Dashboard alert when automation forecasts the hiring period ending today',
              }),
            };
          }
          return {
            description: intl.formatMessage(
              {
                defaultMessage:
                  'Your hiring goal ends in {daysLeft} {daysWord}. Extend the duration if you need more time to hire.',
                description:
                  'Dashboard alert when automation forecasts the hiring period ending soon',
              },
              {
                daysLeft,
                daysWord: 'days',
              },
            ),
          };
        }
        break;
      }
      case 'target_budget_met': {
        if (automationAction === 'paused') {
          return {
            description: intl.formatMessage({
              defaultMessage:
                'Budget allocated for the opening fully used. Campaigns are paused, increase your budget to resume.',
              description:
                'Dashboard inline alert: 100% of opening spend used; add budget to continue',
            }),
          };
        }
        if (automationAction === 'breached') {
          return {
            description: intl.formatMessage(
              {
                defaultMessage:
                  "You've used {budgetPercentage}% of this opening's budget. Review if you need to increase it.",
                description:
                  'Dashboard alert when a large share of the opening budget has been used',
              },
              {
                budgetPercentage: thresholdPercentage,
              },
            ),
          };
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  return { getAutomationFlashMessages };
};
